import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "../context/auth-context";
import { generateMetaData } from "../util";
import { useIpDetails } from "./location-service";
import { parseApiResponse } from "../models";
import { fetchServiceJson } from "./service-fetch";

export async function fetchPublicOrganisationInfo(profileId, token) {
  if (!profileId) return null;

  const response = await fetchServiceJson(
    `organisation/public/${profileId}/info`,
    token,
  );

  return response ? parseApiResponse(response) : null;
}

export async function fetchOrganisationMemberFromUser({ orgId, userId, token }) {
  if (!orgId || !userId) return null;

  const response = await fetchServiceJson(
    `organisation/${orgId}/user/${userId}/info?noExtraParam=true`,
    token,
  );

  return response ? parseApiResponse(response).data : null;
}

export async function joinCommunity({ orgId, userId, token }) {
  const response = await fetchServiceJson(
    `organisation/${orgId}/user/${userId}/join`,
    token,
    {
      method: "POST",
      rejectOnError: true,
    },
  );

  return response ? parseApiResponse(response).data?.member : null;
}

export async function requestToJoinCommunity({ orgId, userId, token }) {
  const response = await fetchServiceJson(
    `organisation/${orgId}/user/${userId}/request`,
    token,
    {
      method: "POST",
      data: {},
      rejectOnError: true,
    },
  );

  return response ? parseApiResponse(response).data : null;
}

export async function withdrawJoinRequest({ orgId, userId, token }) {
  const response = await fetchServiceJson(
    `organisation/${orgId}/user/${userId}/withdraw`,
    token,
    {
      method: "DELETE",
      rejectOnError: true,
    },
  );

  return response ? parseApiResponse(response).data : null;
}

export async function fetchStorefrontEnabledOrganisations(token) {
  const response = await fetchServiceJson(
    "organisation/organisations/storefront/enabled?page=1&limit=10&searchTerm=phone",
    token,
  );

  return response ? parseApiResponse(response) : null;
}

export const getOrganisationQueryKey = (orgId, token) => [
  "organisation",
  orgId,
  token,
];

export function useJoinCommunity({ ...config }) {
  const client = useClient();
  const { data } = useIpDetails();
  return useMutation(
    (params) =>
      client(`organisation/${params.orgId}/user/${params.userId}/join`, {
        data: {
          ...params,
          ...generateMetaData(data),
        },
      }),
    {
      ...config,
    },
  );
}

export function useCheckUserMembership({ ...config }) {
  const client = useClient();
  return useMutation(
    (params) => {
      const endpoint = `organisation/${params.orgId}/user/${params.userId}/info`;
      return client(endpoint, {}).then(parseApiResponse);
    },
    {
      ...config,
    },
  );
}

export function useFetchOrganisation({
  orgId,
  token,
  enabled = false,
  ...config
} = {}) {
  const client = useClient();
  return useQuery(
    getOrganisationQueryKey(orgId, token),
    () => {
      const url = `organisation/${orgId}/info`;
      const requestConfig = {
        method: "GET",
      };

      if (token) {
        requestConfig.headers = {
          Authorization: `Bearer ${token}`,
        };
      }

      return client(url, requestConfig).then(parseApiResponse);
    },
    {
      enabled: !!enabled && !!orgId,
      staleTime: Infinity,
      cacheTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      ...config,
    },
  );
}
